import { Alert, AlertTitle, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { findRingMovedOnSheets } from "../util/stationMonitor";
import StationExceptionLine from "./StationExceptionLine";
import BannerList from "./BannerList";

// #3476 -- one banner naming every started sheet the ring has moved past: the
// station, its ring, the ride and the seat. A sheet counts once a later ride in
// the same ring has marks (a ring that moved on, not idle minutes), so a ride
// still being scored never shows here. Staff-side only: the scribe's tablet gets
// no warning (the scribe cannot act on it, and an Unsent sheet is never deleted
// from the tablet). The last ride of the day has nothing after it, so it is
// caught at class final as an Unfinished ride instead. Driven by the same rows
// as the other banners (score-api's `ringMovedOn`); rendered by the Station
// Monitor page and the dashboard strip through StationMonitorBanners.
export default function RingMovedOnBanner({ rows, compact = false }) {
  const { t } = useTranslation("scoreadmin");
  const sheets = findRingMovedOnSheets(rows);
  if (sheets.length === 0) return null;
  if (compact) {
    return (
      <StationExceptionLine
        severity="warning"
        variant="standard"
        testId="ring-moved-on-banner"
        text={t("stationMonitor.ringMovedOn.compact", { count: sheets.length })}
      />
    );
  }

  const rideText = (s) => {
    if (s.rideNumber !== null && s.riderName) return t("stationMonitor.ringMovedOn.ride", { number: s.rideNumber, rider: s.riderName });
    if (s.rideNumber !== null) return t("stationMonitor.ringMovedOn.rideNoRider", { number: s.rideNumber });
    if (s.riderName) return t("stationMonitor.ringMovedOn.rideNoNumber", { rider: s.riderName });
    return t("stationMonitor.ringMovedOn.rideUnknown");
  };

  return (
    <Alert severity="warning" variant="standard" role="alert" data-testid="ring-moved-on-banner" sx={{ mb: 2 }}>
      <AlertTitle>{t("stationMonitor.ringMovedOn.title")}</AlertTitle>
      {/* #3682: at most 3 sheets, then "and N more". */}
      <BannerList
        testId="ring-moved-on-list"
        items={sheets}
        renderItem={(s) => {
          const named = Boolean(s.stationLabel);
          const vars = { station: s.stationLabel, ring: s.ring, ride: rideText(s) };
          return (
            <Typography variant="body2" data-testid={`ring-moved-on-${s.deviceId}-${s.rideNumber ?? "x"}`}>
              {named ? t("stationMonitor.ringMovedOn.line", vars) : t("stationMonitor.ringMovedOn.lineUnnamed", vars)}
              {s.seat && ` ${t("stationMonitor.ringMovedOn.seat", { seat: s.seat })}`}
            </Typography>
          );
        }}
      />
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {t("stationMonitor.ringMovedOn.hint")}
      </Typography>
    </Alert>
  );
}
